// Publish confirmation modal (Phase 5A UI polish).
//
// Before a version goes live the admin sees exactly what will happen: which
// version, that both languages are complete, whether acceptance is re-required,
// and roughly how many organizations it will apply to. Publishing is the point a
// real agreement becomes effective, so it is a deliberate, reviewed action.

import { useEffect, useState } from 'react'
import { CheckCircle2, Users } from 'lucide-react'
import { DialogContent } from '../../../components/ui/dialog'
import { Dialog } from '../../../components/ui/dialog.lib'
import { Button } from '../../../components/ui/button'
import { AgreementsError } from '../../../api/agreements'
import * as api from '../../../api/agreementsAdmin'
import type { ApplicabilityRule } from '../../../api/agreementsAdmin'
import { useT } from '../../../i18n'
import { policiesI18n } from '../../../i18n/admin/policies'
import { SUPPORTED_AGREEMENT_LOCALES, agreementLocaleShortLabel } from '../../../i18n/agreement-locales'

function audienceSummary(rules: ApplicabilityRule[], t: (typeof policiesI18n)['en']): string {
  const parts: string[] = []
  if (rules.some((r) => r.effect === 'require' && r.scope === 'buy')) parts.push(t.resellers)
  if (rules.some((r) => r.effect === 'require' && r.scope === 'supply')) parts.push(t.vendors)
  const spec = rules.filter((r) => r.effect === 'require' && r.org_id).length
  const exempt = rules.filter((r) => r.effect === 'exempt' && r.org_id).length
  if (spec) parts.push(`${spec} ${t.specificOrgs.toLowerCase()}`)
  if (exempt) parts.push(`${exempt} ${t.exemptOrgs.toLowerCase()}`)
  return parts.length ? parts.join(' · ') : t.audienceNone
}

export function PublishConfirmModal({
  agreementId,
  versionId,
  versionNumber,
  reacceptRequired,
  audience,
  onClose,
  onPublished,
}: {
  agreementId: string
  versionId: string
  versionNumber: number
  reacceptRequired: boolean
  audience: ApplicabilityRule[]
  onClose: () => void
  onPublished: () => void | Promise<void>
}) {
  const t = useT(policiesI18n)
  const [count, setCount] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    api
      .fetchAudienceCount(agreementId)
      .then((r) => alive && setCount(r.count))
      .catch(() => alive && setCount(null))
    return () => {
      alive = false
    }
  }, [agreementId])

  async function publish() {
    setBusy(true)
    setError(null)
    try {
      await api.publishVersion(agreementId, versionId)
      await onPublished()
    } catch (e) {
      setError(e instanceof AgreementsError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md gap-0 p-0">
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">{t.publishTitle}</h2>
          <p className="mt-0.5 text-xs text-slate-500">{t.publishSubtitle}</p>
        </div>

        <div className="space-y-3 px-5 py-4 text-sm">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700">
              {error}
            </div>
          )}
          <Row label={t.version}>{versionNumber}</Row>
          <Row label={t.languages}>
            <span className="flex items-center gap-2">
              {SUPPORTED_AGREEMENT_LOCALES.map((loc) => (
                <span key={loc} className="inline-flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {agreementLocaleShortLabel(loc)}
                </span>
              ))}
            </span>
          </Row>
          <Row label={t.reacceptRequired}>{reacceptRequired ? t.yes : t.no}</Row>
          <Row label={t.audience}>{audienceSummary(audience, t)}</Row>
          <Row label={t.affectedOrgs}>
            <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
              <Users className="h-3.5 w-3.5 text-slate-400" />
              {count === null ? '—' : `~${count}`}
            </span>
          </Row>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            {t.cancel}
          </Button>
          <Button size="sm" onClick={publish} disabled={busy} data-testid="confirm-publish-btn">
            {t.confirmPublish}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-slate-500">{label}</span>
      <span className="text-right text-slate-800">{children}</span>
    </div>
  )
}
